import { useRef, useEffect, useCallback } from 'react';
import { usePageVisibility } from 'react-page-visibility';

interface UseWakeLockReturn {
  /** Manually request the wake lock (useful for tying to a user gesture on iOS) */
  requestWakeLock: () => Promise<void>;
  /** Manually release the wake lock */
  releaseWakeLock: () => Promise<void>;
}

const isWakeLockSupported = (): boolean => 'wakeLock' in navigator;

/**
 * Keeps the screen awake while the consuming component is mounted and the page
 * is visible. Automatically re-acquires the lock when the page regains
 * visibility (the browser releases wake locks on visibility change).
 *
 * Releases the lock on unmount or when the page becomes hidden.
 */
export function useWakeLock(): UseWakeLockReturn {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const isPageVisible = usePageVisibility();

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch {
        // Already released or not held
      }
      wakeLockRef.current = null;
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!isWakeLockSupported()) return;
    if (wakeLockRef.current) return;

    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch {
      // Request denied — e.g. low battery, or iOS requires a user gesture
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isPageVisible) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [isPageVisible, requestWakeLock, releaseWakeLock]);

  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  return { requestWakeLock, releaseWakeLock };
}
